/**
 * Survey Access Mode Utilities
 *
 * Helper functions to convert between SurveyAccessMode and
 * the backend's isPublic/requiresInvitation boolean flags.
 */

import type { Survey } from '@/types';
import type { SurveyAccessMode } from '@/types/invitation';

/**
 * Derive the access mode from a survey's isPublic and requiresInvitation flags
 *
 * Mapping:
 * - isPublic=true, requiresInvitation=false → 'public'
 * - isPublic=false, requiresInvitation=true → 'invitation'
 * - isPublic=false, requiresInvitation=false → 'private'
 *
 * @param survey - Survey object with isPublic and requiresInvitation flags
 * @returns The derived access mode
 */
export function getAccessMode(survey: Survey): SurveyAccessMode {
  if (survey.isPublic && !survey.requiresInvitation) {
    return 'public';
  }

  if (!survey.isPublic && survey.requiresInvitation) {
    return 'invitation';
  }

  // Default: private (isPublic=false, requiresInvitation=false)
  return 'private';
}

/**
 * Convert an access mode to the backend's boolean flags
 *
 * Mapping:
 * - 'public' → isPublic=true, requiresInvitation=false
 * - 'invitation' → isPublic=false, requiresInvitation=true
 * - 'private' → isPublic=false, requiresInvitation=false
 *
 * @param mode - The access mode to convert
 * @returns Object with isPublic and requiresInvitation flags
 */
export function getAccessModeFlags(mode: SurveyAccessMode): {
  isPublic: boolean;
  requiresInvitation: boolean;
} {
  switch (mode) {
    case 'public':
      return {
        isPublic: true,
        requiresInvitation: false,
      };

    case 'invitation':
      return {
        isPublic: false,
        requiresInvitation: true,
      };

    case 'private':
      return {
        isPublic: false,
        requiresInvitation: false,
      };

    default: {
      // Exhaustiveness check - TypeScript will error if new modes are added
      const _exhaustiveCheck: never = mode;
      throw new Error(`Unknown access mode: ${_exhaustiveCheck}`);
    }
  }
}

/**
 * Generate the public URL for an invitation token
 *
 * @param token - The invitation token (or survey ID for public surveys)
 * @returns Full URL to access the survey
 */
export function getInvitationUrl(token: string): string {
  return `${window.location.origin}/s/${token}`;
}

/**
 * Generate the public URL for a survey (for public access mode)
 *
 * @param surveyIdOrToken - The survey public token (preferred) or ID (legacy)
 * @returns Full URL to access the survey publicly
 */
export function getPublicSurveyUrl(surveyIdOrToken: string | number): string {
  return `${window.location.origin}/s/${surveyIdOrToken}`;
}

/**
 * Get the shareable URL for a survey based on its access mode
 *
 * - Public: Returns /s/{publicToken} (or fallback to /s/{surveyId} for legacy)
 * - Invitation: Returns null (user must use specific invitation URLs)
 * - Private: Returns null (no public access)
 *
 * @param survey - Survey object
 * @returns Shareable URL or null if not publicly shareable
 */
export function getSurveyShareUrl(survey: Survey): string | null {
  const mode = getAccessMode(survey);

  if (mode === 'public') {
    // Use publicToken if available (secure), fallback to ID (legacy)
    const tokenOrId = survey.publicToken || survey.id;
    return getPublicSurveyUrl(tokenOrId);
  }

  // Invitation and private modes don't have a single shareable URL
  return null;
}

/**
 * Check if a survey allows anonymous responses
 * (public or invitation mode with no login required)
 *
 * @param survey - Survey object
 * @returns True if anonymous responses are allowed
 */
export function allowsAnonymousResponses(survey: Survey): boolean {
  const mode = getAccessMode(survey);
  return mode === 'public' || mode === 'invitation';
}
